import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChevronDown, BookOpen, Video, MessageCircle, LifeBuoy } from 'lucide-react';

const HelpPage = () => {
  const [openFaq, setOpenFaq] = useState(0);

  const faqs = [
    {
      question: 'How do I log in to VITRA?',
      answer: 'Visit the login page and enter your email and password. If you forgot your password, click on "Forgot Password" and follow the instructions to reset it.'
    },
    {
      question: 'How do I add a new patient?',
      answer: 'Navigate to the "Patients" section and click "Add New Patient". Fill in their information and medical history, then save. The patient will receive an invitation to link their account.'
    },
    {
      question: 'How do I set medication reminders?',
      answer: 'Go to the patient\'s profile, select "Medications", and click "Add Medication". Set the dosage, frequency, and reminder times. Notifications will be sent at scheduled times.'
    },
    {
      question: 'Can I communicate with patients directly?',
      answer: 'Yes! Use the "Messages" section to send secure messages to your patients. All communications are encrypted and HIPAA compliant.'
    },
    {
      question: 'How do I schedule appointments?',
      answer: 'Visit the "Appointments" section, click "New Appointment", select the patient and time, and confirm. Both you and the patient will receive notifications.'
    },
    {
      question: 'What should I do if I receive an alert?',
      answer: 'Review the alert details immediately in the "Alerts" section. Take appropriate clinical action and mark the alert as resolved once handled.'
    },
    {
      question: 'How secure is my patient data?',
      answer: 'VITRA uses enterprise-grade encryption, HIPAA-compliant storage, and follows industry best practices for data security. All data is protected with 256-bit encryption.'
    },
    {
      question: 'How do I change my password?',
      answer: 'Click on your profile icon in the header, select "Change Password", enter your current password and new password, then save.'
    }
  ];

  const resources = [
    {
      icon: BookOpen,
      title: 'Documentation',
      description: 'Comprehensive guides and tutorials',
    },
    {
      icon: Video,
      title: 'Video Tutorials',
      description: 'Step-by-step video guides',
    },
    {
      icon: MessageCircle,
      title: 'Live Chat',
      description: 'Chat with support team',
    },
  ];

  return (
    <div className="min-h-screen flex flex-col vitra-atmosphere">
      <Header />
      <main className="flex-grow w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Help Center</h1>
          <p className="text-xl text-gray-600">Find answers to common questions about VITRA</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {resources.map(({ icon: Icon, title, description }) => (
            <div key={title} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
                <Icon className="w-6 h-6" strokeWidth={1.75} aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
              <p className="text-gray-700 text-sm">{description}</p>
            </div>
          ))}
        </div>

        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
          
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === index ? -1 : index)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-semibold text-gray-900 text-left">
                    {faq.question}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-teal-600 transition-transform ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                </button>

                {openFaq === index && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-10 bg-teal-50 border border-teal-200 rounded-lg p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              <LifeBuoy className="w-6 h-6 text-teal-700 flex-shrink-0 mt-0.5" strokeWidth={1.75} aria-hidden="true" />
              <p className="text-gray-700">
                <span className="font-semibold">Still need help?</span> Our support team is here for you.
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 transition-colors font-medium"
            >
              Contact Support
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default HelpPage;
